import moment from 'moment/moment';
import { Heading2 } from '@sb1/ffe-core-react';
import { useSelector } from 'react-redux';
import {
    fetchAverageDonationStatistics,
    fetchNumberOfProjectsStatistics,
    fetchNumberOfUniqueGiversStatistics,
    fetchNumerOfGiverdataTransactionsStatistics,
    fetchNumerOfRecurringGiversStatistics,
    fetchTotalCollectedSumStatistics,
} from '../organization-statistics-actions';
import { formatMoneyKr, formatNumber, getDateFormatted, pluralize } from '../../../../utils';
import { WaveCardWithData } from '../../../../components/wave-card/WaveCardWithData';
import { useCallback, useEffect, useState } from 'react';
import styles from './organization-year-summary.module.less';

const RecurringGiverWaveCard = ({ id, fetchNumberOfRecurringGivers, hasAcceptedRecurringPayment }) => {
    const recurringListLink = '../recurring';
    const recurringSettingsLink = '../recurring/settings';

    const recurringGiversSharedProps = {
        topColor: '#fadde1',
        subText: 'Faste givere',
        hideShadow: true,
        src: 'illustrations/mann_trompet.svg',
        fetchFunc: fetchNumberOfRecurringGivers,
    };

    if (hasAcceptedRecurringPayment) {
        return (
            <WaveCardWithData
                {...recurringGiversSharedProps}
                id={id}
                title={(data) => `${formatNumber(data)} ${pluralize(data, 'person', 'personer')}`}
                subText={'Faste givere'}
                bodyText={(data) => `har blitt ${pluralize(data, 'fast støttespiller', 'faste støttespillere')}`}
                to={recurringListLink}
            />
        );
    }

    return (
        <WaveCardWithData
            {...recurringGiversSharedProps}
            id={id}
            title={'Rekrutter faste givere'}
            subText={'Faste givere'}
            bodyText={(data) =>
                data > 0
                    ? `${formatNumber(data)} ${pluralize(data, 'person', 'personer')} ønsker å bli ${pluralize(
                          data,
                          'fast støttespiller',
                          'faste støttespillere'
                      )}`
                    : `Gå til innstillinger`
            }
            to={recurringSettingsLink}
        />
    );
};

const GiverDataWaveCard = ({ id, organizationId, fetchGiverDataNumber }) => {
    const giverdataLink = '../giverdata';
    const giverdataSettingsLink = '../giverdata/settings';
    const [signed, setSigned] = useState(null);

    const sharedProps = {
        topColor: '#f5d9b3',
        subText: 'Giverdata',
        hideShadow: true,
        src: 'illustrations/snakkebobler.svg',
        fetchFunc: fetchGiverDataNumber,
    };

    useEffect(() => {
        const checkIfGiverDataIsSigned = async () => {
            fetch(`/api/public/organization/${organizationId}/giver-data-signed`)
                .then((response) => response.json())
                .then((body) => setSigned(!!body?.signed));
        };
        checkIfGiverDataIsSigned();
    }, [organizationId]);

    if (signed) {
        return (
            <WaveCardWithData
                {...sharedProps}
                id={id}
                title={(data) => `${formatNumber(data)} ${pluralize(data, 'person', 'personer')}`}
                bodyText={`har sagt at de vil høre fra dere`}
                to={giverdataLink}
            />
        );
    }

    return (
        <WaveCardWithData
            {...sharedProps}
            title={'Samle inn giverdata'}
            bodyText={(data) =>
                data > 0
                    ? `${formatNumber(data)} ${pluralize(data, 'person', 'personer')} ønsker å høre fra dere`
                    : `Gå til innstillinger`
            }
            to={giverdataSettingsLink}
        />
    );
};

export const OrganizationYearSummary = ({
    organizationName,
    organizationId,
    year = moment().year(),
    isOrgIdrettslag,
    hasAcceptedRecurringPayment,
}) => {
    const baseURL = useSelector((s) => s.baseURL);
    const dateFrom = getDateFormatted(moment(year, 'YYYY').startOf('year'));
    const dateTo = getDateFormatted(moment(year, 'YYYY').endOf('year'));

    const fetchCollected = useCallback(
        () => fetchTotalCollectedSumStatistics({ baseURL, organizationId, dateFrom, dateTo }),
        [baseURL, organizationId, dateFrom, dateTo]
    );

    const fetchNumberOfProjects = useCallback(
        () => fetchNumberOfProjectsStatistics({ baseURL, organizationId, dateFrom, dateTo }),
        [baseURL, organizationId, dateFrom, dateTo]
    );

    const fetchNumberOfUniqueGivers = useCallback(
        () => fetchNumberOfUniqueGiversStatistics({ baseURL, organizationId, dateFrom, dateTo }),
        [baseURL, organizationId, dateFrom, dateTo]
    );

    const fetchAverageDonation = useCallback(
        () => fetchAverageDonationStatistics({ baseURL, organizationId, dateFrom, dateTo }),
        [baseURL, organizationId, dateFrom, dateTo]
    );

    const fetchNumberOfRecurringGivers = useCallback(
        () => fetchNumerOfRecurringGiversStatistics({ baseURL, organizationId, dateFrom, dateTo }),
        [baseURL, organizationId, dateFrom, dateTo]
    );

    const fetchGiverDataNumber = useCallback(
        () => fetchNumerOfGiverdataTransactionsStatistics({ baseURL, organizationId, dateFrom, dateTo }),
        [baseURL, organizationId, dateFrom, dateTo]
    );

    return (
        <div className={styles.wrapper}>
            <div className={styles.contentWrapper}>
                <Heading2>Året så langt</Heading2>
                <p>
                    Nøkkeltall for {organizationName} sitt resultat på Spleis i {year}
                </p>
                <div className={styles.cardWrapper}>
                    <WaveCardWithData
                        id={'collected'}
                        topColor={'#f2f2f9'}
                        title={(data) => formatMoneyKr(data)}
                        fetchFunc={fetchCollected}
                        subText={'Innsamlet beløp'}
                        bodyText={'har blitt samlet inn på Spleis'}
                        src={'illustrations/sparegris.svg'}
                        hideShadow={true}
                    />
                    <WaveCardWithData
                        id={'projects'}
                        topColor={'#C2E7DA'}
                        title={(data) => `${formatNumber(data)} ${pluralize(data, 'spleis', 'spleiser')}`}
                        fetchFunc={fetchNumberOfProjects}
                        subText={'Antall innsamlinger'}
                        bodyText={'har blitt startet for dere'}
                        src={'illustrations/hand_hjerte.svg'}
                        hideShadow={true}
                    />
                    <WaveCardWithData
                        id={'givers'}
                        topColor={'#fcece9'}
                        title={(data) => `${formatNumber(data)} fine folk`}
                        fetchFunc={fetchNumberOfUniqueGivers}
                        subText={'Antall givere'}
                        bodyText={'har støttet en spleis for dere'}
                        src={'illustrations/to_personer_klemmer.svg'}
                        hideShadow={true}
                    />
                    {!isOrgIdrettslag && (
                        <>
                            <WaveCardWithData
                                id={'average-donation'}
                                topColor={'#fde8d9'}
                                title={(data) => formatMoneyKr(data)}
                                fetchFunc={fetchAverageDonation}
                                subText={'Snittbeløp donasjoner'}
                                bodyText={'Snittbeløp for donasjoner er'}
                                src={'illustrations/hand_mynter.svg'}
                                flipTitleDisplay={true}
                                hideShadow={true}
                            />
                            <RecurringGiverWaveCard
                                id={'recurring'}
                                organizationId={organizationId}
                                fetchNumberOfRecurringGivers={fetchNumberOfRecurringGivers}
                                hasAcceptedRecurringPayment={hasAcceptedRecurringPayment}
                            />
                            <GiverDataWaveCard
                                id={'giverdata'}
                                organizationId={organizationId}
                                fetchGiverDataNumber={fetchGiverDataNumber}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
